import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import { useOfferBestPrice } from 'hooks/Offers/useOfferBestPrice'
import { memo, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import { OFFER_BOOKINGS_THRESHOLD } from 'constants/offer'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import { getShouldUseSearchPrices } from 'lib/search/searchUtils'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import Group from 'components/utils/Group'
import {
  PRIOR_TO_CHECK_IN_CANCELLATION_POLICIES,
} from 'constants/cancellationPolicies'
import { getRefundableUntilAndIsRefundableFromCancellationPolicy } from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'

const URGENCY_TAG_THRESHOLD = 5
const COMPACT_URGENCY_TAG_LIMIT = 2

type CompactUrgencyTagType = 'countdown' | 'flexibleCancellation' | 'lowRooms' | 'bookings'
type CompactUrgencyPresentation = 'label' | 'text'

const COMPACT_URGENCY_TAG_PRIORITY: Array<CompactUrgencyTagType> = [
  'countdown',
  'flexibleCancellation',
  'lowRooms',
  'bookings',
]

interface Props {
  offer: App.Offer | App.OfferSummary
  filters?: App.OfferListFilters
  /**
   * Check-in used for Flexible cancellation refundability.
   * Prefer the same value as Free cancellation inclusion / TileCancellationPolicy
   * (e.g. HotelSearchTile `cancellationCheckInDate`).
   *
   * - `undefined` (prop omitted): fall back to `filters.checkIn`
   * - `''` (or other string): use as-is — empty means “no dates selected”
   *   (Maldives charter bundle). Do not use `?? filters.checkIn` for that case.
   */
  checkInDate?: string
  cancellationPolicyType?: App.CancellationPolicyType
  timezoneOffset?: number
  variant?: 'default' | 'compact'
  /**
   * Compact-only presentation.
   * - `label`: Optimizely denser tiles (master pill styles)
   * - `text`: CRO-6924 mosaic rows
   * @default label
   */
  presentation?: CompactUrgencyPresentation
}

interface CompactUrgencyTagOptions {
  hasCountdown: boolean
  hasFlexibleCancellation: boolean
  hasLowRooms: boolean
  hasBookings: boolean
}

export function getCompactUrgencyTagTypes(options: CompactUrgencyTagOptions): Array<CompactUrgencyTagType> {
  const visibilityByType: Record<CompactUrgencyTagType, boolean> = {
    countdown: options.hasCountdown,
    flexibleCancellation: options.hasFlexibleCancellation,
    lowRooms: options.hasLowRooms,
    bookings: options.hasBookings,
  }

  return COMPACT_URGENCY_TAG_PRIORITY
    .filter(type => visibilityByType[type])
    .slice(0, COMPACT_URGENCY_TAG_LIMIT)
}

export function resolveFlexibleCancellationCheckInDate(
  checkInDate: string | undefined,
  filtersCheckIn?: string,
): string | undefined {
  // Distinguish omitted prop (fall back) from explicit empty string (no dates).
  return checkInDate !== undefined ? checkInDate : filtersCheckIn
}

export function getShouldShowFlexibleCancellationTag({
  cancellationPolicyType,
  checkInDate,
  timezoneOffset,
  presentation = 'label',
}: {
  cancellationPolicyType?: App.CancellationPolicyType
  checkInDate?: string
  timezoneOffset?: number
  /**
   * CRO mosaic (`text`) renders Free/Flexible cancellation as inclusion rows —
   * never as urgency copy. Optimizely denser tiles (`label`) keep pill tags.
   */
  presentation?: CompactUrgencyPresentation
}) {
  if (presentation === 'text') {
    return false
  }

  if (!cancellationPolicyType || !PRIOR_TO_CHECK_IN_CANCELLATION_POLICIES.includes(cancellationPolicyType)) {
    return false
  }

  const { isRefundable } = getRefundableUntilAndIsRefundableFromCancellationPolicy(
    cancellationPolicyType,
    checkInDate || '',
    timezoneOffset || 0,
  )

  return isRefundable
}

function OfferTilePricingUrgencyTags(props: Props) {
  const {
    cancellationPolicyType,
    offer,
    filters,
    checkInDate,
    timezoneOffset,
    variant = 'default',
    presentation = 'label',
  } = props

  const numRoomsPackage = useOfferBestPrice(offer, filters, { disabled: getShouldUseSearchPrices(filters) })
  const offerMetaData = useOfferMetaData(offer.id, filters)

  const showBookedTodayUrgencyTag = offer.stats.booked >= OFFER_BOOKINGS_THRESHOLD

  const availableRooms = numRoomsPackage?.availableRooms ?? offerMetaData?.availableRooms
  const hasLowRoomsUrgencyTag =
    !!availableRooms &&
    availableRooms !== 0 &&
    availableRooms <= URGENCY_TAG_THRESHOLD &&
    !!filters?.checkIn
  const showLowRoomsUrgencyTag = !showBookedTodayUrgencyTag && hasLowRoomsUrgencyTag

  const countdownUrgencyLabel = useMemo(() => {
    return offer.urgencyTags?.find(label => label.type === 'left' && label.end)
  }, [offer.urgencyTags])
  const flexibleCancellationCheckInDate = resolveFlexibleCancellationCheckInDate(
    checkInDate,
    filters?.checkIn,
  )
  const showFlexibleCancellationTag = getShouldShowFlexibleCancellationTag({
    cancellationPolicyType,
    checkInDate: flexibleCancellationCheckInDate,
    timezoneOffset,
    presentation,
  })
  const compactTagTypes = useMemo(() => getCompactUrgencyTagTypes({
    hasBookings: showBookedTodayUrgencyTag,
    hasCountdown: !!countdownUrgencyLabel,
    hasFlexibleCancellation: showFlexibleCancellationTag,
    hasLowRooms: showLowRoomsUrgencyTag,
  }), [countdownUrgencyLabel, showLowRoomsUrgencyTag, showBookedTodayUrgencyTag, showFlexibleCancellationTag])

  if (variant === 'compact') {
    const tags = compactTagTypes.map((type) => {
      if (type === 'countdown' && countdownUrgencyLabel?.end) {
        return <OfferUrgencyCountdownLabel
          key={type}
          endDate={countdownUrgencyLabel.end}
          tagPlace="offer-labels"
          presentation={presentation}
        />
      }
      if (type === 'flexibleCancellation') {
        return <PromotionLabel key={type}>
          <Caption variant="medium" weight="bold">
            <FormattedMessage
              id="hCwWYZZ-Qge1iETJVsZy"
              defaultMessage="Flexible cancellation"
              
            />
          </Caption>
        </PromotionLabel>
      }
      if (type === 'lowRooms' && availableRooms) {
        if (presentation === 'text') {
          return <BodyText key={type} variant="medium" colour="urgency">
            <FormattedMessage
              id="YWK-9mp9BmGtOL4GLm3i"
              defaultMessage="Only {availableRooms, plural, one {# room} other {# rooms}} left at this price!"
              
              values={{ availableRooms }}
            />
          </BodyText>
        }
        return <MessagingLabel key={type} variant="urgency">
          <FormattedMessage
            id="YWK-9mp9BmGtOL4GLm3i"
            defaultMessage="Only {availableRooms, plural, one {# room} other {# rooms}} left at this price!"
            
            values={{ availableRooms }}
          />
        </MessagingLabel>
      }
      if (type === 'bookings') {
        return <BookingsCountLabel
          key={type}
          bookings={offer.stats.booked}
          threshold="offer"
          presentation={presentation}
        />
      }
      return null
    })

    if (presentation === 'text') {
      return <Group direction="vertical" gap={4}>{tags}</Group>
    }

    return <>{tags}</>
  }

  return <>
    <BookingsCountLabel bookings={offer.stats.booked} threshold="offer" />
    {showLowRoomsUrgencyTag && <MessagingLabel variant="urgency">
      <FormattedMessage
        id="YWK-9mp9BmGtOL4GLm3i"
        defaultMessage="Only {availableRooms, plural, one {# room} other {# rooms}} left at this price!"
        
        values={{ availableRooms }}
      />
    </MessagingLabel>}
  </>
}

export default memo(OfferTilePricingUrgencyTags)
